import type { PaginationState, SortingState } from '@tanstack/react-table';
import { AxiosError } from 'axios';

import {
  type GetScenariosParams,
  type ScenarioListResponse,
  ScenarioOrderBy,
  type ScenarioResponse,
  type ScenarioResponseProgress,
} from '@/api/generated.schemas';

export const SCENARIOS_DEFAULT_PAGE_SIZE = 10;

export const SCENARIOS_PROGRESS_POLL_INTERVAL_MS = 10_000;

export const SCENARIOS_PROGRESS_POLL_MAX_RETRIES = 3;

export const SCENARIOS_NAME_SEARCH_DEBOUNCE_MS = 300;

export const SCENARIO_NAME_MAX_LENGTH = 100;

export const SCENARIOS_DEFAULT_SORTING: SortingState = [
  {
    id: ScenarioOrderBy.creation_date,
    desc: true,
  },
];

export function isScenarioProgressComplete(
  progress: ScenarioResponseProgress | undefined
): boolean {
  return progress === 1;
}

export function getScenarioProgressPollInterval(
  progress: ScenarioResponseProgress | undefined
): number | false {
  return isScenarioProgressComplete(progress)
    ? false
    : SCENARIOS_PROGRESS_POLL_INTERVAL_MS;
}

export function getIncompleteScenarioIds(
  scenarios: ReadonlyArray<Pick<ScenarioResponse, 'id' | 'progress'>>
): Array<string> {
  return scenarios
    .filter((scenario) => !isScenarioProgressComplete(scenario.progress))
    .map((scenario) => scenario.id);
}

export function patchScenarioInListResponse(
  current: ScenarioListResponse | undefined,
  scenario: ScenarioResponse
): ScenarioListResponse | undefined {
  if (current === undefined) {
    return current;
  }

  const existing = current.items.find((item) => item.id === scenario.id);
  if (
    existing === undefined ||
    (existing.progress === scenario.progress &&
      existing.completed_simulation_runs === scenario.completed_simulation_runs)
  ) {
    return current;
  }

  // Only merge progress: a poll that started before a rename must not restore the old name.
  return {
    ...current,
    items: current.items.map((item) =>
      item.id === scenario.id
        ? {
            ...item,
            progress: scenario.progress,
            completed_simulation_runs: scenario.completed_simulation_runs,
          }
        : item
    ),
  };
}

export function replaceScenarioInListResponse(
  current: ScenarioListResponse | undefined,
  scenario: ScenarioResponse
): ScenarioListResponse | undefined {
  if (current === undefined) {
    return current;
  }

  return {
    ...current,
    items: current.items.map((item) =>
      item.id === scenario.id ? scenario : item
    ),
  };
}

export function shouldInvalidateScenariosListForPoll(
  current: ScenarioListResponse | undefined,
  scenario: ScenarioResponse,
  orderBy: GetScenariosParams['order_by']
): boolean {
  if (current === undefined || orderBy === undefined) {
    return false;
  }

  const existing = current.items.find((item) => item.id === scenario.id);
  if (existing === undefined) {
    return false;
  }

  if (orderBy === ScenarioOrderBy.progress) {
    return existing.progress !== scenario.progress;
  }

  if (orderBy === ScenarioOrderBy.completed_simulation_runs) {
    return (
      existing.completed_simulation_runs !== scenario.completed_simulation_runs
    );
  }

  return false;
}

export function removeScenarioFromListResponse(
  current: ScenarioListResponse | undefined,
  scenarioId: string
): ScenarioListResponse | undefined {
  if (current === undefined) {
    return current;
  }

  const items = current.items.filter((item) => item.id !== scenarioId);
  if (items.length === current.items.length) {
    return current;
  }

  return {
    ...current,
    items,
    total: Math.max(0, current.total - 1),
  };
}

export function isNotFoundError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 404;
}

function isScenarioOrderBy(value: string): value is ScenarioOrderBy {
  return Object.values(ScenarioOrderBy).includes(value as ScenarioOrderBy);
}

export function buildScenariosListParams(
  pagination: PaginationState,
  sorting: SortingState,
  nameSearch: string
): GetScenariosParams {
  const sort = sorting.at(0);
  const sortId = sort?.id;
  const orderBy =
    sortId !== undefined && isScenarioOrderBy(sortId)
      ? sortId
      : ScenarioOrderBy.creation_date;
  const name = nameSearch.trim();

  return {
    skip: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    order_by: orderBy,
    descending: sort?.desc ?? true,
    ...(name ? { name } : {}),
  };
}
